import { api, buildQueryParams, URLs } from '@/libs/api'
import type {
  AbsenceListParams,
  AbsenceRecord,
  AbsenceRecordData,
  AbsenceTypeListParams,
  AbsenceTypeRecord,
  AbsenceTypeRecordData,
  ApiResponse,
  CreateAbsencePayload,
  CreateAbsenceTypePayload,
  CursorPaginatedResponse,
  UpdateAbsencePayload,
  UpdateAbsenceTypePayload,
} from '@/libs/interfaces'
import { IS_MOCK_MODE } from '@/libs/mocks/config'
import { absencesApiMock, absenceTypesApiMock } from '@/libs/mocks/handlers/absences'

// ── UI-side types (used by Leaves.tsx calendar) ──────────────────────────────

export type AbsenceType = 'vacation' | 'sick' | 'parental' | 'vab' | 'unpaid' | 'other'

export type AbsenceStatus = 'pending' | 'approved' | 'rejected'

export interface AbsenceEntry {
  id: string
  employeeId: string
  employeeName: string
  office?: string
  type: AbsenceType
  status: AbsenceStatus
  startDate: string
  endDate: string
  partialDay?: boolean
  startTime?: string
  endTime?: string
  comment?: string
  createdAt: string
}

// ── Absence Types API ─────────────────────────────────────────────────────────

const absenceTypesApiReal = {
  create: async (orgID: string, payload: CreateAbsenceTypePayload): Promise<AbsenceTypeRecord> => {
    return await api
      .post<ApiResponse<AbsenceTypeRecord>>(URLs.AbsenceTypes.create(orgID), payload)
      .then((res) => res.data.data)
  },

  list: async (params: AbsenceTypeListParams): Promise<AbsenceTypeRecord[]> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    return await api
      .get<ApiResponse<AbsenceTypeRecordData>>(URLs.AbsenceTypes.list(orgID, query))
      .then((res) => res.data.data.data)
  },

  listCursor: async (
    params: AbsenceTypeListParams,
  ): Promise<CursorPaginatedResponse<AbsenceTypeRecord>> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<AbsenceTypeRecordData>>(URLs.AbsenceTypes.list(orgID, query))
      .then((res) => res.data.data)
    return {
      data: response.data,
      pageInfo: {
        first: response.first,
        prev: response.prev,
        cursor: response.cursor,
        next: response.next,
        last: response.last,
      },
    }
  },

  getByID: async (orgID: string, absenceTypeID: string): Promise<AbsenceTypeRecord> => {
    return await api
      .get<ApiResponse<AbsenceTypeRecord>>(URLs.AbsenceTypes.byID(orgID, absenceTypeID))
      .then((res) => res.data.data)
  },

  update: async (
    orgID: string,
    absenceTypeID: string,
    payload: UpdateAbsenceTypePayload,
  ): Promise<AbsenceTypeRecord> => {
    return await api
      .patch<ApiResponse<AbsenceTypeRecord>>(URLs.AbsenceTypes.byID(orgID, absenceTypeID), payload)
      .then((res) => res.data.data)
  },

  delete: async (orgID: string, absenceTypeID: string): Promise<AbsenceTypeRecord> => {
    return await api
      .delete<ApiResponse<AbsenceTypeRecord>>(URLs.AbsenceTypes.byID(orgID, absenceTypeID))
      .then((res) => res.data.data)
  },
}

export const absenceTypesApi: typeof absenceTypesApiReal = IS_MOCK_MODE
  ? absenceTypesApiMock
  : absenceTypesApiReal

const absencesApiReal = {
  create: async (
    orgID: string,
    employeeID: string,
    payload: CreateAbsencePayload,
  ): Promise<AbsenceRecord> => {
    return await api
      .post<ApiResponse<AbsenceRecord>>(URLs.Absences.create(orgID, employeeID), payload)
      .then((res) => res.data.data)
  },

  list: async (params: AbsenceListParams): Promise<AbsenceRecord[]> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    return await api
      .get<ApiResponse<AbsenceRecordData>>(URLs.Absences.list(orgID, query))
      .then((res) => res.data.data.data)
  },

  listAll: async (params: Omit<AbsenceListParams, 'employeeID'>): Promise<AbsenceRecord[]> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    return await api
      .get<ApiResponse<AbsenceRecordData>>(URLs.Absences.listAll(orgID ?? '', query))
      .then((res) => res.data.data.data)
  },

  listCursor: async (
    params: AbsenceListParams,
  ): Promise<CursorPaginatedResponse<AbsenceRecord>> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<AbsenceRecordData>>(URLs.Absences.list(orgID, query))
      .then((res) => res.data.data)
    return {
      data: response.data,
      pageInfo: {
        first: response.first,
        prev: response.prev,
        cursor: response.cursor,
        next: response.next,
        last: response.last,
      },
    }
  },

  getByID: async (orgID: string, employeeID: string, absenceID: string): Promise<AbsenceRecord> => {
    return await api
      .get<ApiResponse<AbsenceRecord>>(URLs.Absences.getByID(orgID, employeeID, absenceID))
      .then((res) => res.data.data)
  },

  update: async (
    orgID: string,
    employeeID: string,
    absenceID: string,
    payload: UpdateAbsencePayload,
  ): Promise<AbsenceRecord> => {
    return await api
      .patch<ApiResponse<AbsenceRecord>>(
        URLs.Absences.update(orgID, employeeID, absenceID),
        payload,
      )
      .then((res) => res.data.data)
  },

  delete: async (orgID: string, absenceID: string): Promise<AbsenceRecord> => {
    return await api
      .delete<ApiResponse<AbsenceRecord>>(URLs.Absences.delete(orgID, absenceID))
      .then((res) => res.data.data)
  },
}

export const absencesApi: typeof absencesApiReal = IS_MOCK_MODE ? absencesApiMock : absencesApiReal
